/**
 * Date-only helpers.
 *
 * Calendar dates (league season dates, registration deadlines, tournament
 * start/end dates) are stored as DATE (or midnight TIMESTAMP) columns and come
 * back from the API as "YYYY-MM-DD" or as a midnight-UTC ISO string such as
 * "2026-10-17T00:00:00.000Z". Passing those to `new Date()` and formatting in
 * local time shifts the day backwards for every user west of UTC
 * (17 Oct becomes 16 Oct). These helpers treat the value as a calendar date.
 *
 * Do NOT use them for real timestamps (e.g. a match's scheduled_at).
 */

const DATE_PREFIX = /^(\d{4})-(\d{2})-(\d{2})/;

const pad = (n) => String(n).padStart(2, "0");

/**
 * Normalise a date-only value to "YYYY-MM-DD".
 * Accepts "YYYY-MM-DD", an ISO string whose date part is the calendar date,
 * or a Date object. Returns "" for empty / invalid input.
 */
export function toDateOnlyString(value) {
  if (value === null || value === undefined || value === "") return "";

  if (value instanceof Date) {
    if (isNaN(value.getTime())) return "";
    // A Date built from a midnight-UTC value: use the UTC calendar date.
    const isUtcMidnight =
      value.getUTCHours() === 0 &&
      value.getUTCMinutes() === 0 &&
      value.getUTCSeconds() === 0 &&
      value.getUTCMilliseconds() === 0;
    if (isUtcMidnight) {
      return `${value.getUTCFullYear()}-${pad(value.getUTCMonth() + 1)}-${pad(value.getUTCDate())}`;
    }
    return `${value.getFullYear()}-${pad(value.getMonth() + 1)}-${pad(value.getDate())}`;
  }

  const match = DATE_PREFIX.exec(String(value).trim());
  if (match) return `${match[1]}-${match[2]}-${match[3]}`;

  const parsed = new Date(value);
  return isNaN(parsed.getTime()) ? "" : toDateOnlyString(parsed);
}

/**
 * Parse a date-only value into a Date at local midnight of that calendar day,
 * or null when the value is empty / invalid.
 */
export function parseDateOnly(value) {
  const str = toDateOnlyString(value);
  if (!str) return null;
  const [y, m, d] = str.split("-").map(Number);
  const date = new Date(y, m - 1, d);
  return isNaN(date.getTime()) ? null : date;
}

/**
 * Format a date-only value for display.
 * @param {*} value - "YYYY-MM-DD", midnight-UTC ISO string or Date
 * @param {Intl.DateTimeFormatOptions} [options]
 * @param {string} [fallback] - returned for empty / invalid input
 */
export function formatDateOnly(
  value,
  options = { year: "numeric", month: "short", day: "numeric" },
  fallback = ""
) {
  const date = parseDateOnly(value);
  if (!date) return fallback;
  return date.toLocaleDateString("en-US", options);
}

const dateOnly = { toDateOnlyString, parseDateOnly, formatDateOnly };
export default dateOnly;
